import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthenticationService } from './AuthenticationService';
import { AppConfig } from '../app.config';
import { SampleFileRequest } from '../requests/sampleFileRequest';
import { BasicResponse } from '../responses/basicResponse';
import { FileDefinition } from '../models/fileDefinition';
import { CreateFileDefinitionRequest } from '../requests/createFileDefinitionRequest';
import { UpdateFileDefinitionRequest } from '../requests/updateFileDefinitionRequest';
import { TransformationDefinition } from '../models/transformationDefinition';
import { CreateTransformationDefinitionRequest } from '../requests/createTransformationDefinitionRequest';
import { UpdateTransformationDefinitionRequest } from '../requests/updateTransformationDefinitionRequest';
import { DeleteFileDefinitionRequest } from '../requests/deleteFileDefinitionRequest';
import { DeleteTransformationDefinitionRequest } from '../requests/deleteTransformationDefinitionRequest';
import { UndeleteTransformationDefinitionRequest } from '../requests/undeleteTransformationDefinitionRequest';
import { UndeleteFileDefinitionRequest } from '../requests/undeleteFileDefinitionRequest';
import { TransformFileRequest } from '../requests/transformFileRequest';
import { PreviewTransformationRequest } from '../requests/previewTransformationRequest';
import { PublishFileDefinitionRequest } from '../requests/publishFileDefinitionRequest';
import { PublishTransformationDefinitionRequest } from '../requests/publishTransformationDefinitionRequest';
import { FilterDefinition } from '../models/filterDefinition';
import { CreateFilterDefinitionRequest } from '../requests/createFilterDefinitionRequest';
import { UpdateFilterDefinitionRequest } from '../requests/updateFilterDefinitionRequest';
import { DeleteFilterDefinitionRequest } from '../requests/deleteFilterDefinitionRequest';
import { UndeleteFilterDefinitionRequest } from '../requests/undeleteFilterDefinitionRequest';
import { PublishFilterDefinitionRequest } from '../requests/publishFilterDefinitionRequest';
import { PreviewFilterRequest } from '../requests/previewFilterRequest';
import { ConsolidationDefinition } from '../models/consolidationDefinition';
import { CreateConsolidationDefinitionRequest } from '../requests/createConsolidationDefinitionRequest';
import { UpdateConsolidationDefinitionRequest } from '../requests/updateConsolidationDefinitionRequest';
import { DeleteConsolidationDefinitionRequest } from '../requests/deleteConsolidationDefinitionRequest';
import { UndeleteConsolidationDefinitionRequest } from '../requests/undeleteConsolidationDefinitionRequest';
import { PublishConsolidationDefinitionRequest } from '../requests/publishConsolidationDefinitionRequest';
import { PreviewConsolidationRequest } from '../requests/previewConsolidationRequest';
import { ValidationDefinition } from '../models/validationDefinition';
import { CreateValidationDefinitionRequest } from '../requests/createValidationDefinitionRequest';
import { UpdateValidationDefinitionRequest } from '../requests/updateValidationDefinitionRequest';
import { DeleteValidationDefinitionRequest } from '../requests/deleteValidationDefinitionRequest';
import { UndeleteValidationDefinitionRequest } from '../requests/undeleteValidationDefinitionRequest';
import { PublishValidationDefinitionRequest } from '../requests/publishValidationDefinitionRequest';
import { PreviewValidationRequest } from '../requests/previewValidationRequest';
import { AllocationDefinition } from '../models/allocationDefinition';
import { CreateAllocationDefinitionRequest } from '../requests/createAllocationDefinitionRequest';
import { UpdateAllocationDefinitionRequest } from '../requests/updateAllocationDefinitionRequest';
import { DeleteAllocationDefinitionRequest } from '../requests/deleteAllocationDefinitionRequest';
import { UndeleteAllocationDefinitionRequest } from '../requests/undeleteAllocationDefinitionRequest';
import { PublishAllocationDefinitionRequest } from '../requests/publishAllocationDefinitionRequest';
import { PreviewAllocationRequest } from '../requests/previewAllocationRequest';
import { LoggingService } from './LoggingService';
import { DefinitionObject, WizardPreviewRequest } from '../requests/wizardPreviewRequest';
import { blobToBase64 } from '../helpers/blob.utils';

@Injectable({
  providedIn: 'root'
})
export class FileDefinitionApiService {

  constructor(private httpClient: HttpClient, private appConfig:AppConfig, private authService:AuthenticationService, private logger:LoggingService) { }

  get serviceURL():string {
    return this.appConfig.getConfig("filedefinitionapi_external_url") + '/api/filedefinition/';
  }
  get optionsJson(){
    return this.appConfig.optionsJson;
  }
  get optionsForm(){
    return this.appConfig.optionsForm;
  }
  
  public sampleFile(file:File, fileType:string, fieldDelimiter:string, hasHeader:boolean){
    let r = new SampleFileRequest();
    r.requestorId=this.authService.currentUserValue!.id;
    r.type=fileType;
    r.fieldDelimiter=fieldDelimiter;
    r.hasHeader=hasHeader;
    r.sampleRowAmount=10;
    let body = JSON.stringify(r);
    const formData: FormData = new FormData();
    formData.append('requestString', body);
    formData.append('files', file, file.name);
    this.logger.log("Uploading file to sample ");
    return this.httpClient.post<any>(this.serviceURL+`sampleFile`, formData, this.optionsForm).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public transformFile(file:File, transformationDefinitionId:number){
    let r = new TransformFileRequest();
    r.requestorId=this.authService.currentUserValue!.id;
    r.transformationDefinitionId=transformationDefinitionId;
    let body = JSON.stringify(r);
    const formData: FormData = new FormData();
    formData.append('requestString', body);
    formData.append('files', file, file.name);
    this.logger.log("Uploading file to transform ");
    return this.httpClient.post<any>(this.serviceURL+`transformFile`, formData, this.optionsForm).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  //#region file definitions handling
  public getFileDefinitions(requestorEntityId:number|undefined, requestorEntityType:string|undefined){
    this.logger.log("Getting file definitions");
    return this.httpClient.get<any>(this.serviceURL + `fileDefinitions/`+ (requestorEntityId?(requestorEntityId):'') + (requestorEntityType?('/' + requestorEntityType):''), this.optionsForm).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public createFileDefinition(requestorId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined, fd:FileDefinition):Observable<any>{
    let r = new CreateFileDefinitionRequest();
    r.requestorId=requestorId;
    r.requestorEntityId=requestorEntityId;
    r.requestorEntityType=requestorEntityType;
    r.fileDefinition = FileDefinition.mapToDTO(fd);
    return this.httpClient.post<any>(this.serviceURL+`createFileDefinition`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public updateFileDefinition(requestorId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined, fd:FileDefinition, indexTracker:any[]):Observable<any>{
    let r = new UpdateFileDefinitionRequest();
    r.requestorId = requestorId;
    r.requestorEntityId=requestorEntityId;
    r.requestorEntityType=requestorEntityType;
    r.fileDefinition = FileDefinition.mapToDTO(fd);
    r.indexTracker=indexTracker;
    return this.httpClient.put<any>(this.serviceURL+`updateFileDefinition`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public deleteFileDefinition(requestorId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined, fileDefinitionId:number){
    this.logger.log("Deleting file definition");
    let r=new DeleteFileDefinitionRequest();
    r.requestorId=requestorId;
    r.requestorEntityId=requestorEntityId;
    r.requestorEntityType=requestorEntityType;
    r.fileDefinitionId = fileDefinitionId;
    r.physicalDelete = false;
    return this.httpClient.request<any>('delete', this.serviceURL+`deleteFileDefinition`, {body: r, headers: this.optionsJson.headers}).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public undeleteFileDefinition(requestorId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined, fileDefinitionId:number){
    this.logger.log("Undeleting file definition");
    let r=new UndeleteFileDefinitionRequest();
    r.requestorId=requestorId;
    r.requestorEntityId=requestorEntityId;
    r.requestorEntityType=requestorEntityType;
    r.fileDefinitionId = fileDefinitionId;
    return this.httpClient.put<any>(this.serviceURL+'undeleteFileDefinition', r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public publishFileDefinition(requestorId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined, fileDefinitionId:number, published:boolean){
    this.logger.log("Publish file definition");
    let r=new PublishFileDefinitionRequest();
    r.requestorId=requestorId;
    r.requestorEntityId=requestorEntityId;
    r.requestorEntityType=requestorEntityType;
    r.fileDefinitionId = fileDefinitionId;
    r.published=published;
    return this.httpClient.put<any>(this.serviceURL+ 'publishFileDefinition', r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getScopeRestrictions(){
    this.logger.log("Getting scope restrictions");
    return this.httpClient.get<any>(this.serviceURL+ 'scopeRestrictions', this.optionsForm).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getFileDefinitionDependants(fileDefinitionId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined){
    this.logger.log("Getting file definition dependants");
    return this.httpClient.get<any>(this.serviceURL+ 'fileDefinitionDependants/' + fileDefinitionId + (requestorEntityId?('/' +requestorEntityId):'') + (requestorEntityType?('/' + requestorEntityType):''), this.optionsForm).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }
  //#endregion

  //#region transformation definitions handling
  public getTransformationRuleDefinitions(){
    this.logger.log("Getting transformation rule definitions");
    return this.httpClient.get<any>(this.serviceURL+`transformationRuleDefinitions`, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getTransformationDefinitions(requestorEntityId:number|undefined, requestorEntityType:string|undefined){
    this.logger.log("Getting transformation definitions");
    return this.httpClient.get<any>(this.serviceURL+`transformationDefinitions/` + (requestorEntityId?(requestorEntityId):'') + (requestorEntityType?('/' + requestorEntityType):''), this.optionsForm).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getTransformationDefinition(requestorEntityId:number|undefined, requestorEntityType:string|undefined, transformationDefinitionId:number){
    this.logger.log("Getting transformation definition");
    return this.httpClient.get<any>(this.serviceURL+`transformationDefinition/` + transformationDefinitionId + '/' + (requestorEntityId?(requestorEntityId):'') + (requestorEntityType?('/' + requestorEntityType):'') , this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public createTransformationDefinition(requestorId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined, td:TransformationDefinition):Observable<any>{
    let r = new CreateTransformationDefinitionRequest();
    r.requestorId=requestorId;
    r.requestorEntityId=requestorEntityId;
    r.requestorEntityType=requestorEntityType;
    r.transformationDefinition = TransformationDefinition.mapToDTO(td);
    return this.httpClient.post<any>(this.serviceURL+`createTransformationDefinition`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public updateTransformationDefinition(requestorId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined, td:TransformationDefinition):Observable<any>{
    let r = new UpdateTransformationDefinitionRequest();
    r.requestorId = requestorId;
    r.requestorEntityId=requestorEntityId;
    r.requestorEntityType=requestorEntityType;
    r.transformationDefinition = TransformationDefinition.mapToDTO(td);
    return this.httpClient.put<any>(this.serviceURL+`updateTransformationDefinition`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public deleteTransformationDefinition(requestorId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined, transformationDefinitionId:number){
    this.logger.log("Deleting transformation definition");
    let r=new DeleteTransformationDefinitionRequest();
    r.requestorId=requestorId;
    r.requestorEntityId=requestorEntityId;
    r.requestorEntityType=requestorEntityType;
    r.transformationDefinitionId = transformationDefinitionId;
    r.physicalDelete = false;
    return this.httpClient.request<any>('delete', this.serviceURL+`deleteTransformationDefinition`, {body: r, headers: this.optionsJson.headers}).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public undeleteTransformationDefinition(requestorId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined, transformationDefinitionId:number){
    this.logger.log("Undeleting transformation definition");
    let r=new UndeleteTransformationDefinitionRequest();
    r.requestorId=requestorId;
    r.requestorEntityId=requestorEntityId;
    r.requestorEntityType=requestorEntityType;
    r.transformationDefinitionId = transformationDefinitionId;
    return this.httpClient.put<any>(this.serviceURL+'undeleteTransformationDefinition', r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public publishTransformationDefinition(requestorId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined, transformationDefinitionId:number, published:boolean){
    this.logger.log("Publish transformation definition");
    let r=new PublishTransformationDefinitionRequest();
    r.requestorId=requestorId;
    r.requestorEntityId=requestorEntityId;
    r.requestorEntityType=requestorEntityType;
    r.transformationDefinitionId = transformationDefinitionId;
    r.published=published;
    return this.httpClient.put<any>(this.serviceURL+ 'publishTransformationDefinition', r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public previewTransformation(file:File, transformationDefinitionId:number){
    let r = new PreviewTransformationRequest();
    r.requestorId=this.authService.currentUserValue!.id;
    r.requestorEntityId=this.authService.entityContextId;
    r.requestorEntityType=this.authService.entityContextType;
    r.transformationDefinitionId=transformationDefinitionId;
    let body = JSON.stringify(r);
    const formData: FormData = new FormData();
    formData.append('requestString', body);
    formData.append('files', file, file.name);
    this.logger.log("Uploading file to preview transformation");
    return this.httpClient.post<any>(this.serviceURL+`previewTransformation`, formData, this.optionsForm).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getTransformationDefinitionDependants(transformationDefinitionId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined){
    this.logger.log("Getting transformation definition dependants");
    return this.httpClient.get<any>(this.serviceURL+ 'transformationDefinitionDependants/' + transformationDefinitionId + (requestorEntityId?('/' +requestorEntityId):'') + (requestorEntityType?('/' + requestorEntityType):''), this.optionsForm).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }
  //#endregion

  //#region filter definitions handling
  public getFilterRuleDefinitions(){
    this.logger.log("Getting filter rule definitions");
    return this.httpClient.get<any>(this.serviceURL+`filterRuleDefinitions`, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getFilterDefinitions(requestorEntityId:number|undefined, requestorEntityType:string|undefined){
    this.logger.log("Getting filter definitions");
    return this.httpClient.get<any>(this.serviceURL+`filterDefinitions/` + (requestorEntityId?(requestorEntityId):'') + (requestorEntityType?('/' + requestorEntityType):''), this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getFilterDefinition(requestorEntityId:number|undefined, requestorEntityType:string|undefined, filterDefinitionId:number){
    this.logger.log("Getting filter definition");
    return this.httpClient.get<any>(this.serviceURL+`filterDefinition/` + filterDefinitionId + (requestorEntityId?('/' +requestorEntityId):'') + (requestorEntityType?('/' + requestorEntityType):''), this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public createFilterDefinition(requestorId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined, fd:FilterDefinition):Observable<any>{
    let r = new CreateFilterDefinitionRequest();
    r.requestorId=requestorId;
    r.requestorEntityId=requestorEntityId;
    r.requestorEntityType=requestorEntityType;
    r.filterDefinition = FilterDefinition.mapToDTO(fd);
    return this.httpClient.post<any>(this.serviceURL+`createFilterDefinition`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public updateFilterDefinition(requestorId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined, fd:FilterDefinition):Observable<any>{
    let r = new UpdateFilterDefinitionRequest();
    r.requestorId = requestorId;
    r.requestorEntityId=requestorEntityId;
    r.requestorEntityType=requestorEntityType;
    r.filterDefinition = FilterDefinition.mapToDTO(fd);
    return this.httpClient.put<any>(this.serviceURL+`updateFilterDefinition`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public deleteFilterDefinition(requestorId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined, filterDefinitionId:number){
    this.logger.log("Deleting filter definition");
    let r=new DeleteFilterDefinitionRequest();
    r.requestorId=requestorId;
    r.requestorEntityId=requestorEntityId;
    r.requestorEntityType=requestorEntityType;
    r.filterDefinitionId = filterDefinitionId;
    r.physicalDelete = false;
    return this.httpClient.request<any>('delete', this.serviceURL+`deleteFilterDefinition`, {body: r, headers: this.optionsJson.headers}).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public undeleteFilterDefinition(requestorId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined, filterDefinitionId:number){
    this.logger.log("Undeleting filter definition");
    let r=new UndeleteFilterDefinitionRequest();
    r.requestorId=requestorId;
    r.requestorEntityId=requestorEntityId;
    r.requestorEntityType=requestorEntityType;
    r.filterDefinitionId = filterDefinitionId;
    return this.httpClient.put<any>(this.serviceURL+'undeleteFilterDefinition', r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public publishFilterDefinition(requestorId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined, filterDefinitionId:number, published:boolean){
    this.logger.log("Publish filter definition");
    let r=new PublishFilterDefinitionRequest();
    r.requestorId=requestorId;
    r.requestorEntityId=requestorEntityId;
    r.requestorEntityType=requestorEntityType;
    r.filterDefinitionId = filterDefinitionId;
    r.published=published;
    return this.httpClient.put<any>(this.serviceURL+ 'publishFilterDefinition', r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public previewFilter(file:File, filterDefinitionId:number){
    let r = new PreviewFilterRequest();
    r.requestorId=this.authService.currentUserValue!.id;
    r.requestorEntityId=this.authService.entityContextId;
    r.requestorEntityType=this.authService.entityContextType;
    r.filterDefinitionId=filterDefinitionId;
    let body = JSON.stringify(r);
    const formData: FormData = new FormData();
    formData.append('requestString', body);
    formData.append('files', file, file.name);
    this.logger.log("Uploading file to preview filtering");
    return this.httpClient.post<any>(this.serviceURL+`previewFilter`, formData, this.optionsForm).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }
  //#endregion

  //#region validation definitions handling
  public getValidationRuleDefinitions(){
    this.logger.log("Getting validation rule definitions");
    return this.httpClient.get<any>(this.serviceURL+`validationRuleDefinitions`, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getValidationDefinitions(requestorEntityId:number|undefined, requestorEntityType:string|undefined){
    this.logger.log("Getting validation definitions");
    return this.httpClient.get<any>(this.serviceURL+`validationDefinitions/` + (requestorEntityId?(requestorEntityId):'') + (requestorEntityType?('/' + requestorEntityType):''), this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getValidationDefinition(requestorEntityId:number|undefined, requestorEntityType:string|undefined, validationDefinitionId:number){
    this.logger.log("Getting validation definition");
    return this.httpClient.get<any>(this.serviceURL+`validationDefinition/` + validationDefinitionId + (requestorEntityId?('/' +requestorEntityId):'') + (requestorEntityType?('/' + requestorEntityType):''), this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public createValidationDefinition(requestorId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined, vd:ValidationDefinition):Observable<any>{
    let r = new CreateValidationDefinitionRequest();
    r.requestorId=requestorId;
    r.requestorEntityId=requestorEntityId;
    r.requestorEntityType=requestorEntityType;
    r.validationDefinition = ValidationDefinition.mapToDTO(vd);
    return this.httpClient.post<any>(this.serviceURL+`createValidationDefinition`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public updateValidationDefinition(requestorId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined, vd:ValidationDefinition):Observable<any>{
    let r = new UpdateValidationDefinitionRequest();
    r.requestorId = requestorId;
    r.requestorEntityId=requestorEntityId;
    r.requestorEntityType=requestorEntityType;
    r.validationDefinition = ValidationDefinition.mapToDTO(vd);
    return this.httpClient.put<any>(this.serviceURL+`updateValidationDefinition`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public deleteValidationDefinition(requestorId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined, validationDefinitionId:number){
    this.logger.log("Deleting validation definition");
    let r=new DeleteValidationDefinitionRequest();
    r.requestorId=requestorId;
    r.requestorEntityId=requestorEntityId;
    r.requestorEntityType=requestorEntityType;
    r.validationDefinitionId = validationDefinitionId;
    r.physicalDelete = false;
    return this.httpClient.request<any>('delete', this.serviceURL+`deleteValidationDefinition`, {body: r, headers: this.optionsJson.headers}).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public undeleteValidationDefinition(requestorId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined, validationDefinitionId:number){
    this.logger.log("Undeleting validation definition");
    let r=new UndeleteValidationDefinitionRequest();
    r.requestorId=requestorId;
    r.requestorEntityId=requestorEntityId;
    r.requestorEntityType=requestorEntityType;
    r.validationDefinitionId = validationDefinitionId;
    return this.httpClient.put<any>(this.serviceURL+'undeleteValidationDefinition', r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public publishValidationDefinition(requestorId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined, validationDefinitionId:number, published:boolean){
    this.logger.log("Publish validation definition");
    let r=new PublishValidationDefinitionRequest();
    r.requestorId=requestorId;
    r.requestorEntityId=requestorEntityId;
    r.requestorEntityType=requestorEntityType;
    r.validationDefinitionId = validationDefinitionId;
    r.published=published;
    return this.httpClient.put<any>(this.serviceURL+ 'publishValidationDefinition', r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public previewValidation(file:File, validationDefinitionId:number){
    let r = new PreviewValidationRequest();
    r.requestorId=this.authService.currentUserValue!.id;
    r.requestorEntityId=this.authService.entityContextId;
    r.requestorEntityType=this.authService.entityContextType;
    r.validationDefinitionId=validationDefinitionId;
    let body = JSON.stringify(r);
    const formData: FormData = new FormData();
    formData.append('requestString', body);
    formData.append('files', file, file.name);
    this.logger.log("Uploading file to preview validation");
    return this.httpClient.post<any>(this.serviceURL+`previewValidation`, formData, this.optionsForm).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }
  //#endregion

  //#region meta data handling
  public getMetaDataRuleDefinitions(){
    this.logger.log("Getting meta data rule definitions");
    return this.httpClient.get<any>(this.serviceURL+`metaDataRuleDefinitions`, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }
  //#endregion
  
  //#region consolidation definitions handling
  public getConsolidationRuleDefinitions(){
    this.logger.log("Getting consolidation rule definitions");
    return this.httpClient.get<any>(this.serviceURL+`consolidationRuleDefinitions`, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getConsolidationDefinitions(requestorEntityId:number|undefined, requestorEntityType:string|undefined){
    this.logger.log("Getting consolidation definitions");
    return this.httpClient.get<any>(this.serviceURL+`consolidationDefinitions/` + (requestorEntityId?(requestorEntityId):'') + (requestorEntityType?('/' + requestorEntityType):''), this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getConsolidationDefinition(requestorEntityId:number|undefined, requestorEntityType:string|undefined, consolidationDefinitionId:number){
    this.logger.log("Getting consolidation definition");
    return this.httpClient.get<any>(this.serviceURL+`consolidationDefinition/` + consolidationDefinitionId + (requestorEntityId?('/' +requestorEntityId):'') + (requestorEntityType?('/' + requestorEntityType):''), this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public createConsolidationDefinition(requestorId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined, cd:ConsolidationDefinition):Observable<any>{
    let r = new CreateConsolidationDefinitionRequest();
    r.requestorId=requestorId;
    r.requestorEntityId=requestorEntityId;
    r.requestorEntityType=requestorEntityType;
    r.consolidationDefinition = ConsolidationDefinition.mapToDTO(cd);
    return this.httpClient.post<any>(this.serviceURL+`createConsolidationDefinition`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public updateConsolidationDefinition(requestorId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined, cd:ConsolidationDefinition):Observable<any>{
    let r = new UpdateConsolidationDefinitionRequest();
    r.requestorId = requestorId;
    r.requestorEntityId=requestorEntityId;
    r.requestorEntityType=requestorEntityType;
    r.consolidationDefinition = ConsolidationDefinition.mapToDTO(cd);
    return this.httpClient.put<any>(this.serviceURL+`updateConsolidationDefinition`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public deleteConsolidationDefinition(requestorId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined, consolidationDefinitionId:number){
    this.logger.log("Deleting consolidation definition");
    let r=new DeleteConsolidationDefinitionRequest();
    r.requestorId=requestorId;
    r.requestorEntityId=requestorEntityId;
    r.requestorEntityType=requestorEntityType;
    r.consolidationDefinitionId = consolidationDefinitionId;
    r.physicalDelete = false;
    return this.httpClient.request<any>('delete', this.serviceURL+`deleteConsolidationDefinition`, {body: r, headers: this.optionsJson.headers}).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public undeleteConsolidationDefinition(requestorId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined, consolidationDefinitionId:number){
    this.logger.log("Undeleting consolidation definition");
    let r=new UndeleteConsolidationDefinitionRequest();
    r.requestorId=requestorId;
    r.requestorEntityId=requestorEntityId;
    r.requestorEntityType=requestorEntityType;
    r.consolidationDefinitionId = consolidationDefinitionId;
    return this.httpClient.put<any>(this.serviceURL+'undeleteConsolidationDefinition', r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public publishConsolidationDefinition(requestorId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined, consolidationDefinitionId:number, published:boolean){
    this.logger.log("Publish consolidation definition");
    let r=new PublishConsolidationDefinitionRequest();
    r.requestorId=requestorId;
    r.requestorEntityId=requestorEntityId;
    r.requestorEntityType=requestorEntityType;
    r.consolidationDefinitionId=consolidationDefinitionId;
    r.published=published;
    return this.httpClient.put<any>(this.serviceURL+ 'publishConsolidationDefinition', r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public previewConsolidation(file:File, consolidationDefinitionId:number){
    let r = new PreviewConsolidationRequest();
    r.requestorId=this.authService.currentUserValue!.id;
    r.requestorEntityId=this.authService.entityContextId;
    r.requestorEntityType=this.authService.entityContextType;
    r.consolidationDefinitionId=consolidationDefinitionId;
    let body = JSON.stringify(r);
    const formData: FormData = new FormData();
    formData.append('requestString', body);
    formData.append('files', file, file.name);
    this.logger.log("Uploading file to preview consolidation");
    return this.httpClient.post<any>(this.serviceURL+`previewConsolidation`, formData, this.optionsForm).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }
  //#endregion

  //#region allocation definitions handling
  public getAllocationDefinitions(requestorEntityId:number|undefined, requestorEntityType:string|undefined){
    this.logger.log("Getting allocation definitions");
    return this.httpClient.get<any>(this.serviceURL+`allocationDefinitions/` + (requestorEntityId?(requestorEntityId):'') + (requestorEntityType?('/' + requestorEntityType):''), this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getAllocationDefinition(requestorEntityId:number|undefined, requestorEntityType:string|undefined, allocationDefinitionId:number){
    this.logger.log("Getting allocation definition");
    return this.httpClient.get<any>(this.serviceURL+`allocationDefinition/` + allocationDefinitionId + (requestorEntityId?('/' +requestorEntityId):'') + (requestorEntityType?('/' + requestorEntityType):''), this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public createAllocationDefinition(requestorId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined, ad:AllocationDefinition):Observable<any>{
    let r = new CreateAllocationDefinitionRequest();
    r.requestorId=requestorId;
    r.requestorEntityId=requestorEntityId;
    r.requestorEntityType=requestorEntityType;
    r.allocationDefinition = AllocationDefinition.mapToDTO(ad);
    return this.httpClient.post<any>(this.serviceURL+`createAllocationDefinition`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public updateAllocationDefinition(requestorId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined, ad:AllocationDefinition):Observable<any>{
    let r = new UpdateAllocationDefinitionRequest();
    r.requestorId = requestorId;
    r.requestorEntityId=requestorEntityId;
    r.requestorEntityType=requestorEntityType;
    r.allocationDefinition = AllocationDefinition.mapToDTO(ad);
    return this.httpClient.put<any>(this.serviceURL+`updateAllocationDefinition`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public deleteAllocationDefinition(requestorId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined, allocationDefinitionId:number){
    this.logger.log("Deleting allocation definition");
    let r=new DeleteAllocationDefinitionRequest();
    r.requestorId=requestorId;
    r.requestorEntityId=requestorEntityId;
    r.requestorEntityType=requestorEntityType;
    r.allocationDefinitionId = allocationDefinitionId;
    r.physicalDelete = false;
    return this.httpClient.request<any>('delete', this.serviceURL+`deleteAllocationDefinition`, {body: r, headers: this.optionsJson.headers}).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public undeleteAllocationDefinition(requestorId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined, allocationDefinitionId:number){
    this.logger.log("Undeleting allocation definition");
    let r=new UndeleteAllocationDefinitionRequest();
    r.requestorId=requestorId;
    r.requestorEntityId=requestorEntityId;
    r.requestorEntityType=requestorEntityType;
    r.allocationDefinitionId = allocationDefinitionId;
    return this.httpClient.put<any>(this.serviceURL+'undeleteAllocationDefinition', r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public publishAllocationDefinition(requestorId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined, allocationDefinitionId:number, published:boolean){
    this.logger.log("Publish allocation definition");
    let r=new PublishAllocationDefinitionRequest();
    r.requestorId=requestorId;
    r.requestorEntityId=requestorEntityId;
    r.requestorEntityType=requestorEntityType;
    r.allocationDefinitionId=allocationDefinitionId;
    r.published=published;
    return this.httpClient.put<any>(this.serviceURL+ 'publishAllocationDefinition', r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public previewAllocation(file:File, allocationDefinitionId:number){
    let r = new PreviewAllocationRequest();
    r.requestorId=this.authService.currentUserValue!.id;
    r.requestorEntityId=this.authService.entityContextId;
    r.requestorEntityType=this.authService.entityContextType;
    r.allocationDefinitionId=allocationDefinitionId;
    let body = JSON.stringify(r);
    const formData: FormData = new FormData();
    formData.append('requestString', body);
    formData.append('files', file, file.name);
    this.logger.log("Uploading file to preview allocation");
    return this.httpClient.post<any>(this.serviceURL+`previewAllocation`, formData, this.optionsForm).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }
  //#endregion

  //#region wizard related methods
  public async wizardPreview(file:File, fileDefinition:FileDefinition, filterDefinition:FilterDefinition|undefined, transformationDefinition:TransformationDefinition|undefined){
    const base64WithPrefix = await blobToBase64(file) as string;
    // Remove "data:application/...;base64," prefix to get pure base64
    let r = new WizardPreviewRequest();
    r.base64InputFile = base64WithPrefix;
    let def_object = new DefinitionObject();
      def_object.type = 'file';
      def_object.sequenceNo = 0;
      let dto=FileDefinition.mapToDTO(fileDefinition);
      def_object.definitionDTO = this.addPropertyToBeginning(dto,'$type','file');
      r.definitionList.push(def_object);
    if(filterDefinition){
      let def_object = new DefinitionObject();
      def_object.type = 'filter';
      def_object.sequenceNo = 1;
      let dto = FilterDefinition.mapToDTO(filterDefinition);
      def_object.definitionDTO = this.addPropertyToBeginning(dto,'$type','filter');
      r.definitionList.push(def_object);
    }
    if(transformationDefinition){
      let def_object = new DefinitionObject();
      def_object.type = 'transformation';
      def_object.sequenceNo = 2;
      let dto = TransformationDefinition.mapToDTO(transformationDefinition);
      def_object.definitionDTO = this.addPropertyToBeginning(dto,'$type','transformation');
      r.definitionList.push(def_object);
    }
    let body = JSON.stringify(r);
    const formData: FormData = new FormData();
    formData.append('requestString', body);
    formData.append('files', file, file.name);
    this.logger.log("Uploading file for wizard preview");
    return this.httpClient.post<any>(this.serviceURL+`wizardPreview`, formData, this.optionsForm).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  private addPropertyToBeginning(obj: any, key: string, value: any): any {
    // Use a new object to ensure the order is maintained
    const newObj: any = {
      [key]: value, // The new property comes first
      ...obj         // Then spread the rest of the original object's properties
    };
    return newObj;
  }
  //#endregion

  private createBasicResponseError(error: HttpErrorResponse){
    let br = new BasicResponse();
    br.success=false;
    br.message='Backend error - please try again later';
    return br;
  }

  private handleError(error: HttpErrorResponse) {
    if (error.error instanceof ErrorEvent) {
      // A client-side or network error occurred. Handle it accordingly.
      console.error('An error occurred:', error.error.message);
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong,
      console.error(
        `Backend returned code ${error.status}, ` +
        `body was: ${error.error}`);
    }
    // return an observable with a user-facing error message
    return throwError(()=>(error));
  }
}
